import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Compass, Menu, Moon, Sun, Volume2, VolumeX, X } from "lucide-react";
import { KD9LogoCircle } from "@/components/KD9Logo";
import DailyHoroscope from "@/components/DailyHoroscope";
import JourneyGlyph from "@/components/JourneyGlyph";
import { useAuth } from "@/context/AuthContext";
import useJourneyAudio from "@/hooks/useJourneyAudio";
import api from "@/lib/api";
import {
  JOURNEY_ART, JOURNEY_SPRITES, JOURNEY_DESTINATIONS, ambienceFor, cameraFor,
  destinationFor, initialJourney, isInteractiveTarget, journeyReducer,
  liveDestinations, rememberPortal, stepDestination, swipeDirection, writeJourneyPrefs,
} from "@/lib/egyptJourney";
import "@/components/EgyptJourney.css";

function JourneyDialog({ title, onClose, children, kind }) {
  const ref = useRef(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog.showModal();
    return () => { if (dialog.open) dialog.close(); };
  }, []);
  return <dialog ref={ref} className={`journey-dialog journey-dialog-${kind}`} aria-labelledby="journey-dialog-title" onCancel={(event) => { event.preventDefault(); onClose(); }} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="journey-dialog-inner">
      <div className="journey-dialog-heading"><h2 id="journey-dialog-title">{title}</h2><button className="journey-icon-button" onClick={onClose} aria-label="Volver al territorio" autoFocus><X size={20} /></button></div>
      {children}
    </div>
  </dialog>;
}

function Explorer({ avatar, phase = "idle", sprite, className = "" }) {
  return <span aria-hidden="true" className={`journey-explorer ${avatar === "woman" ? "is-woman" : "is-man"} is-${phase} ${className}`} style={{ backgroundImage: `url("${sprite}")` }} />;
}

export default function EgyptJourney({ config = {}, onExit, previewMode = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();
  const [state, dispatch] = useReducer(journeyReducer, null, () => initialJourney(config, previewMode));
  const [viewport, setViewport] = useState({ width: typeof window === "undefined" ? 1280 : window.innerWidth, height: typeof window === "undefined" ? 800 : window.innerHeight });
  const [panel, setPanel] = useState(null);
  const [live, setLive] = useState([]);
  const [visible, setVisible] = useState(true);
  const [clock, setClock] = useState(() => new Date().getHours());
  const [imageReady, setImageReady] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [panorama, setPanorama] = useState(config.journey_panorama_url || JOURNEY_ART);
  const [sprite, setSprite] = useState(config.journey_sprite_url || JOURNEY_SPRITES);
  const sceneRef = useRef(null);
  const ctaRef = useRef(null);
  const touchRef = useRef(null);
  const pointerRef = useRef(null);
  const pendingRef = useRef(null);
  const enteringRef = useRef(false);
  const previousFocus = useRef(null);
  const lowPower = useMemo(() => typeof navigator !== "undefined" && ((navigator.hardwareConcurrency || 8) <= 4 || (navigator.deviceMemory || 8) <= 4 || navigator.connection?.saveData), []);
  const quiet = Boolean(reduceMotion || state.calm || lowPower);
  const audio = useJourneyAudio(config.ambient_sound !== false);
  const { enabled: soundEnabled, step: playStep } = audio;
  const destinations = useMemo(() => JOURNEY_DESTINATIONS.map((item) => ({ ...item, label: config.portals?.[item.key]?.title || item.label, description: config.portals?.[item.key]?.description || item.description, x: config.portals?.[item.key]?.x ?? item.x, y: config.portals?.[item.key]?.y ?? item.y })), [config.portals]);
  const selected = destinations.find((item) => item.key === state.selectedKey) || destinations[2];
  const camera = cameraFor(selected, viewport, state.phase === "entering" && !quiet);
  const ambience = ambienceFor(config.ambient_cycle, clock);
  const ready = Boolean(state.avatar);

  useEffect(() => {
    const node = sceneRef.current;
    const update = () => setViewport({ width: node.clientWidth, height: node.clientHeight });
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(node);
    window.addEventListener("resize", update);
    return () => { observer?.disconnect(); window.removeEventListener("resize", update); };
  }, []);

  useEffect(() => {
    const visibility = () => setVisible(!document.hidden);
    const timer = window.setInterval(() => setClock(new Date().getHours()), 60000);
    document.addEventListener("visibilitychange", visibility);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", visibility); };
  }, []);

  useEffect(() => {
    writeJourneyPrefs({ avatar: state.avatar, selectedKey: state.selectedKey, illuminated: state.illuminated, calm: state.calm }, previewMode);
  }, [state.avatar, state.selectedKey, state.illuminated, state.calm, previewMode]);

  useEffect(() => {
    if (state.phase === "walking") {
      const timer = window.setTimeout(() => dispatch({ type: "arrive" }), quiet ? 0 : state.duration);
      return () => window.clearTimeout(timer);
    }
    if (["arrived", "awakening"].includes(state.phase)) {
      const timer = window.setTimeout(() => dispatch({ type: "settle" }), quiet ? 0 : state.phase === "awakening" ? 1400 : 850);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [state.phase, state.selectedKey, state.duration, quiet]);

  useEffect(() => {
    if (state.phase !== "walking" || !soundEnabled || !visible || quiet) return undefined;
    playStep();
    const timer = window.setInterval(playStep, 240);
    return () => window.clearInterval(timer);
  }, [state.phase, soundEnabled, playStep, visible, quiet]);

  useEffect(() => {
    if (config.show_live_activity === false || !visible) { setLive([]); return undefined; }
    let active = true;
    const controller = new AbortController();
    const refresh = () => api.get("/api/live/public", { signal: controller.signal })
      .then(({ data }) => { if (active) setLive(liveDestinations(data)); })
      .catch(() => { if (active) setLive([]); });
    refresh();
    const timer = window.setInterval(refresh, 60000);
    return () => { active = false; controller.abort(); window.clearInterval(timer); };
  }, [config.show_live_activity, visible]);

  useEffect(() => () => {
    if (pendingRef.current) window.clearTimeout(pendingRef.current);
    if (pointerRef.current) window.cancelAnimationFrame(pointerRef.current);
  }, []);

  useEffect(() => { setPanorama(config.journey_panorama_url || JOURNEY_ART); setImageReady(false); setImageFailed(false); }, [config.journey_panorama_url]);
  useEffect(() => {
    const source = config.journey_sprite_url || JOURNEY_SPRITES;
    setSprite(source);
    if (source === JOURNEY_SPRITES) return undefined;
    const image = new Image();
    image.onerror = () => setSprite(JOURNEY_SPRITES);
    image.src = source;
    return () => { image.onerror = null; };
  }, [config.journey_sprite_url]);

  const vibrate = useCallback((pattern = 8) => {
    if (!quiet && config.haptics_enabled === true && typeof navigator?.vibrate === "function") navigator.vibrate(pattern);
  }, [quiet, config.haptics_enabled]);

  const closePanel = useCallback(() => {
    setPanel(null);
    window.requestAnimationFrame(() => previousFocus.current?.focus?.({ preventScroll: true }));
  }, []);
  const openPanel = (next) => { previousFocus.current = document.activeElement; setPanel(next); };
  const select = (key) => {
    if (enteringRef.current) return;
    dispatch({ type: "select", key, quiet });
    audio.cue(destinationFor(key));
    vibrate();
  };
  const choose = (avatar) => {
    dispatch({ type: "choose", avatar, quiet });
    vibrate();
    window.requestAnimationFrame(() => sceneRef.current?.focus({ preventScroll: true }));
  };
  const enter = (destination = selected, immediate = false) => {
    if (enteringRef.current) return;
    if (destination.key === "daily") { select("daily"); openPanel("daily"); return; }
    enteringRef.current = true;
    dispatch({ type: "enter" });
    audio.cue(destination);
    vibrate([8, 40, 8]);
    const cross = () => { rememberPortal(destination); audio.stop(); navigate(destination.to); };
    if (quiet || immediate) cross();
    else pendingRef.current = window.setTimeout(cross, 560);
  };
  const cancelEntry = () => {
    if (pendingRef.current) window.clearTimeout(pendingRef.current);
    enteringRef.current = false;
    dispatch({ type: "cancel" });
  };
  const onKey = (event) => {
    if (event.key === "Escape" && !panel) { event.preventDefault(); cancelEntry(); return; }
    if (isInteractiveTarget(event.target)) return;
    if (!ready || panel) return;
    if (["ArrowLeft", "ArrowRight"].includes(event.key)) {
      event.preventDefault(); select(stepDestination(selected.key, event.key === "ArrowRight" ? 1 : -1));
    } else if (event.key === "Enter") { event.preventDefault(); enter(); }
  };
  const onPointerMove = (event) => {
    if (quiet || event.pointerType !== "mouse" || panel) return;
    const x = event.clientX; const y = event.clientY;
    if (pointerRef.current) return;
    pointerRef.current = window.requestAnimationFrame(() => {
      pointerRef.current = null;
      const node = sceneRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      node.style.setProperty("--look-x", `${((x - rect.left) / rect.width - .5) * 9}px`);
      node.style.setProperty("--look-y", `${((y - rect.top) / rect.height - .5) * 5}px`);
    });
  };

  return <main ref={sceneRef} tabIndex={0} className={`kd9-journey ${quiet ? "is-quiet" : ""} ${!visible ? "is-paused" : ""} ${state.illuminated ? "is-lit" : ""} is-${state.phase} ${!ready ? "is-choosing" : ""}`} data-testid="egypt-entry-experience" data-experience-version="journey-v2" data-ambience={ambience} data-selected={selected.key} style={{ "--portal-accent": selected.accent, "--travel-ms": `${state.duration}ms` }} onKeyDown={onKey} onPointerMove={onPointerMove} onPointerLeave={() => { sceneRef.current?.style.setProperty("--look-x", "0px"); sceneRef.current?.style.setProperty("--look-y", "0px"); }}>
    <div className="journey-scene" data-testid="journey-scene" onTouchStart={(event) => {
      if (isInteractiveTarget(event.target) || event.touches.length !== 1) { touchRef.current = null; return; }
      touchRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }} onTouchEnd={(event) => {
      const touch = event.changedTouches[0];
      const direction = swipeDirection(touchRef.current, touch && { x: touch.clientX, y: touch.clientY });
      touchRef.current = null;
      if (direction && ready) select(stepDestination(selected.key, direction));
    }}>
      <div className="journey-camera" data-testid="journey-camera" style={{ width: camera.width, height: camera.height, transform: `translate3d(${camera.x}px, ${camera.y}px, 0) scale(${camera.scale})` }}>
        <div className="journey-world" style={{ "--focus-x": `${selected.x * 100}%`, "--focus-y": `${selected.y * 100}%` }}>
          <img className={`journey-panorama ${imageReady ? "is-ready" : ""}`} src={panorama} width="2172" height="724" alt="" fetchPriority="high" decoding="async" draggable="false" onLoad={() => setImageReady(true)} onError={() => { if (panorama !== JOURNEY_ART) setPanorama(JOURNEY_ART); else { setImageFailed(true); setImageReady(true); } }} />
          <div className="journey-world-shade" aria-hidden="true" />
          <div className="journey-lantern-focus" aria-hidden="true" />
          <div className="journey-awakening" aria-hidden="true" />
          {destinations.map((item, index) => <button key={item.key} type="button" className={`journey-hotspot ${item.key === selected.key ? "is-selected" : ""} ${item.key === "temple" && live.length ? "is-live" : ""}`} style={{ left: `${item.x * 100}%`, top: `${item.y * 100}%`, "--destination-color": item.accent, "--wake-delay": `${index * 120}ms` }} aria-label={`${item.key === selected.key ? "Entrar a" : "Explorar"} ${item.label}`} aria-pressed={item.key === selected.key} data-testid={item.key === "daily" ? "egypt-daily-energy" : `egypt-landmark-${item.key}`} onClick={() => item.key === selected.key ? enter(item) : select(item.key)} disabled={!ready || enteringRef.current} tabIndex={!ready ? -1 : 0}>
            <span className="journey-hotspot-ring" aria-hidden="true" /><span className="journey-hotspot-label">{item.label}{item.key === "temple" && live.length > 0 && <span className="journey-live-dot" aria-label="En vivo" />}</span>
          </button>)}
        </div>
      </div>
      <div className="journey-atmosphere" aria-hidden="true" />
      <div className="journey-foreground" aria-hidden="true" />
      {state.phase === "awakening" && <div className="journey-nine-pulse" aria-hidden="true">9<small>EL TERRITORIO HA DESPERTADO</small></div>}
      {!quiet && Array.from({ length: 9 }, (_, index) => <span key={index} aria-hidden="true" className="journey-dust" style={{ left: `${(index * 13 + 7) % 100}%`, top: `${30 + (index * 11) % 48}%`, animationDelay: `${index * -.8}s` }} />)}
    </div>

    <header className="journey-header">
      <a href="/" className="journey-brand" aria-label="Khaos Delta 9, inicio"><KD9LogoCircle size={44} glow={false} /><span>KHAOS DELTA 9<small>La energía que despierta lo ancestral</small></span></a>
      <div className="journey-header-actions">
        {previewMode && <span className="journey-preview">Vista previa</span>}
        <a href={user ? "/app/inicio" : "/login"} className="journey-access">{user ? "Mi panel" : "Acceder"}<ArrowRight size={14} /></a>
        <button type="button" className="journey-icon-button" onClick={() => openPanel("destinations")} aria-label="Abrir destinos y ajustes" data-testid="journey-menu"><Menu size={21} /></button>
      </div>
    </header>

    <div className="journey-heading">
      <span className="journey-eyebrow">{ready ? "EL TERRITORIO ANCESTRAL" : "BIENVENIDO A TU RECORRIDO"}</span>
      <h1>{ready ? "Tu camino comienza aquí." : "Hay territorios que se despiertan."}</h1>
      {!ready && <p>Elige quién lleva tu luz.</p>}
    </div>

    {ready && <div className={`journey-character ${state.direction < 0 ? "faces-left" : ""}`} aria-hidden="true">
      <span className="journey-contact-shadow" />
      <Explorer avatar={state.avatar} phase={state.phase} sprite={sprite} />
      <span className="journey-foot-dust" />
    </div>}

    {!ready ? <section className="journey-choice" aria-label="Elige tu explorador">
      <div className="journey-choice-figures">{["man", "woman"].map((avatar) => <button key={avatar} type="button" className="journey-choice-button" data-testid={`egypt-avatar-${avatar}`} onClick={() => choose(avatar)}><Explorer avatar={avatar} sprite={sprite} /><span>{avatar === "man" ? "Explorador" : "Exploradora"}</span></button>)}</div>
      <button className="journey-text-button" onClick={() => choose("man")}>Explorar directamente <ArrowRight size={15} /></button>
    </section> : <footer className="journey-dock">
      <div className="journey-compass-line"><span>{String(destinations.findIndex((item) => item.key === selected.key) + 1).padStart(2, "0")} / 06</span><span>{selected.place}</span><span aria-hidden="true"><JourneyGlyph glyph="𓂀" /></span></div>
      <div className="journey-destination-card">
        <div className="journey-destination-copy" aria-live="polite" aria-atomic="true"><span className="journey-destination-glyph" aria-hidden="true"><JourneyGlyph glyph={selected.glyph} /></span><div><h2>{selected.label}</h2><p>{selected.description}</p>{selected.key === "temple" && live.length > 0 && <div className="journey-live-copy"><i /> En vivo · {live[0].title || live[0].first_name || "Encuentro KD9"}</div>}</div></div>
        <button ref={ctaRef} className="journey-enter" onClick={() => enter()} disabled={state.phase === "entering"} data-testid="journey-enter">{state.phase === "entering" ? "Cruzando el umbral…" : selected.key === "daily" ? "Consultar mi energía" : `Entrar a ${selected.label}`}<ArrowRight size={18} /></button>
      </div>
      <div className="journey-navigation">
        <button className="journey-icon-button" aria-label="Destino anterior" onClick={() => select(stepDestination(selected.key, -1))}><ArrowLeft size={19} /></button>
        <nav aria-label="Destinos del territorio" className="journey-destination-tabs">{destinations.map((item) => <button key={item.key} aria-label={item.label} aria-pressed={selected.key === item.key} onClick={() => select(item.key)} data-testid={`journey-tab-${item.key}`}><span aria-hidden="true"><JourneyGlyph glyph={item.glyph} /></span><span className="journey-tab-name">{item.label}</span></button>)}</nav>
        <button className="journey-icon-button" aria-label="Destino siguiente" onClick={() => select(stepDestination(selected.key, 1))}><ArrowRight size={19} /></button>
      </div>
      <div className="journey-bottom-controls">
        {config.show_illuminate !== false && <button onClick={() => { dispatch({ type: "light", quiet }); if (!state.illuminated) audio.cue(destinationFor("daily")); vibrate(); }} data-testid="egypt-illuminate" aria-pressed={state.illuminated}>{state.illuminated ? <Sun size={15} /> : <Moon size={15} />}{state.illuminated ? "Territorio iluminado" : "Despertar el territorio"}</button>}
        {config.ambient_sound !== false && <button onClick={audio.toggle} aria-pressed={audio.enabled} aria-label={audio.enabled ? "Apagar sonido" : "Activar sonido"} data-testid="egypt-sound">{audio.enabled ? <Volume2 size={16} /> : <VolumeX size={16} />}<span>Sonido {audio.enabled ? "activo" : "apagado"}</span></button>}
      </div>
    </footer>}

    {!imageReady && <div className="journey-loading" role="status">El territorio está despertando…</div>}
    {imageFailed && <p className="journey-loading" role="status">Puedes acceder a todos los destinos desde el menú.</p>}
    {state.phase === "entering" && <div className="journey-crossing" role="status"><span><JourneyGlyph glyph={selected.glyph} /></span><p>ENTRANDO A</p><strong>{selected.label}</strong></div>}
    {panel && <JourneyDialog kind={panel} title={panel === "daily" ? "El obelisco · Energía diaria" : "Tu territorio KD9"} onClose={closePanel}>
      {panel === "daily" ? <DailyHoroscope embedded /> : <>
        <p className="journey-menu-intro">Elige un destino para acceder directamente.</p>
        <nav className="journey-direct-destinations" aria-label="Acceso directo">{destinations.map((item) => item.key === "daily" ? <button key={item.key} onClick={() => { select(item.key); setPanel("daily"); }}><span><JourneyGlyph glyph={item.glyph} /></span>{item.label}<ArrowRight size={16} /></button> : <a key={item.key} href={item.to} onClick={() => { rememberPortal(item); audio.stop(); }}><span><JourneyGlyph glyph={item.glyph} /></span>{item.label}<ArrowRight size={16} /></a>)}</nav>
        <div className="journey-settings"><button onClick={() => dispatch({ type: "calm" })} aria-pressed={state.calm}><Compass size={18} /> Movimiento suave {state.calm ? "activado" : "automático"}</button><button onClick={() => { closePanel(); dispatch({ type: "avatar" }); }}>Cambiar explorador</button>{config.allow_skip !== false && <button onClick={() => { audio.stop(); onExit?.(); }} data-testid="egypt-classic">Ver portada clásica</button>}</div>
        <p className="journey-keyboard-hint">← → para explorar · Enter para entrar · Escape para cerrar</p>
      </>}
    </JourneyDialog>}
  </main>;
}
